import { useEffect, useState } from "react";
import { Siren } from "lucide-react";
import { api } from "../../api";
import { trackEvent } from "../../analytics";
import EmergencyMode from "./EmergencyMode";

const ROUTES = [
  { id: "skin", label: "Skin contact" },
  { id: "eye", label: "Eye contact" },
  { id: "inhale", label: "Inhalation" },
  { id: "ingest", label: "Ingestion" },
];

export default function FirstAid({ chemKey, chemName, onRequestQuote }) {
  const [route, setRoute] = useState("skin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emergency, setEmergency] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getFirstAid(chemKey, route)
      .then(setData)
      .finally(() => setLoading(false));
    trackEvent("first_aid_route_viewed", { chemical: chemKey, route });
  }, [chemKey, route]);

  return (
    <section className="panel" id="first-aid" data-testid="section-first-aid">
      <div className="wrap">
        <div className="panel-head">
          <div className="eyebrow">Tool · flagship</div>
          <h2>First-Aid Finder</h2>
          <p>
            Select the exposure route. Steps are specific to the active substance above.
            When something has actually happened, hit <b>Start Emergency</b> — full-screen mode
            with a live flush timer.
          </p>
        </div>

        <div className="route-tabs" data-testid="first-aid-route-tabs">
          {ROUTES.map((r) => (
            <button
              key={r.id}
              className={"route-tab" + (route === r.id ? " on" : "")}
              onClick={() => setRoute(r.id)}
              data-testid={`first-aid-route-${r.id}`}
            >
              {r.label}
            </button>
          ))}
          <button
            className="btn-emergency-launch"
            onClick={() => setEmergency(true)}
            data-testid="first-aid-start-emergency"
          >
            <Siren size={16} /> Start Emergency
          </button>
        </div>

        {loading || !data ? (
          <p className="note">Loading…</p>
        ) : (
          <div className="aid-grid">
            <div className="card" data-testid="first-aid-steps-card">
              <h3 style={{ fontSize: 18, marginBottom: 14 }}>
                {ROUTES.find((r) => r.id === route)?.label} — {chemName}
              </h3>
              <ol className="step-list">
                {data.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              <div className="not-box">
                <h4>Do not</h4>
                <ul>
                  {data.not.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
              <div className="helpline">
                <span>
                  National Poison Control (India): <b>1800-11-6117</b>
                </span>
                <span>
                  Plant EHS extension: <b>x-4400</b>
                </span>
              </div>
              <div className="card-actions">
                <button
                  className="btn btn-emergency"
                  onClick={() => setEmergency(true)}
                  data-testid="first-aid-emergency-btn"
                >
                  <Siren size={16} /> Start Emergency ({ROUTES.find((r) => r.id === route)?.label.toLowerCase()})
                </button>
                <button
                  className="btn btn-brand btn-sm"
                  onClick={() => onRequestQuote?.("first-aid")}
                  data-testid="first-aid-request-quote"
                >
                  Request a quote for {chemName}
                </button>
              </div>
            </div>

            <div className="card" data-testid="first-aid-meta-card">
              <h4 className="card-label">Substance snapshot</h4>
              <div className="meta-row">
                <span>Common grades</span>
                <span>{data.meta.grades}</span>
              </div>
              <div className="meta-row">
                <span>GHS classification</span>
                <span>{data.meta.ghs}</span>
              </div>
              <div className="meta-row">
                <span>Primary route of concern</span>
                <span>{data.meta.primary_route}</span>
              </div>
              <div className="meta-row">
                <span>Neutralizing agent (never on skin)</span>
                <span>{data.meta.neutral_agent}</span>
              </div>
              <h4 className="card-label" style={{ marginTop: 16 }}>
                NFPA 704
              </h4>
              <div className="nfpa">
                <div className="h">{data.chemical.nfpa.h}</div>
                <div className="f">{data.chemical.nfpa.f}</div>
                <div className="r">{data.chemical.nfpa.r}</div>
                <div className="s">{data.chemical.nfpa.s}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <EmergencyMode
        open={emergency}
        onClose={() => setEmergency(false)}
        chemKey={chemKey}
        chemName={chemName}
        route={route}
      />
    </section>
  );
}
